import { Redirect, Route, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { routes, previewRoutes } from './list'
import NoAccess from '@pages/NoAccess'
import NoMobile from '@pages/NoMobile'
import { selectProfile, selectIsAuth } from '@store/profile.reducer'
import { useScreenWidth } from '@hooks/useScreenWidth'
// import Layout from '@common/Layout'
import Technical from '@common/Technical'
import HelpDeskEddyWidget from '@common/HelpDeskEddyWidget/HelpDeskEddyWidget'
import Layout from '@common/Layout/Layout'
import { useFcmMessaging } from '@/hooks/useFcmMessaging'
import { Suspense, useEffect, useLayoutEffect, useState } from 'react'
import Preloader from '@UI/Preloader'
import { REQUIRED_DOCUMENTS_ACCESS_RESTORED_EVENT } from '@/helpers/new/requiredDocumentsCompliance.utils'
import RequiredSurveyModal from '@/components/modals/RequiredSurvey'
import RequiredNumberConfirmationModal from '@/components/modals/RequiredNumberConfirmation'
import RequiredLessonReportModal from '@/components/modals/RequiredLessonReport'
import RequiredDocumentsComplianceModal from '@/components/modals/RequiredDocumentsCompliance'
import useIsTechSupport from '@/components/common/Chat/SupportChatButton/useIsTechSupport'

import NoAuthLayout from '../components/common/Layout/NoAuthLayout'
import useDefaultAuthRedirect from './components/useDefaultAuthRedirect'
import { RequiredActivateTelegramBot } from '@/components/modals/ActivateTelegramBot'
import { RequiredActivateTelegramNotificationBot } from '@/components/modals/ActivateTelegramNotificationBot'
import { RequiredActivateVkNotificationCommunity } from '@/components/modals/ActivateVkNotificationCommunity'
import { RequiredActivateMaxNotificationBot } from '@/components/modals/ActivateMaxNotificationBot'
import { RequiredActivateMaxBot } from '@/components/modals/ActivateMaxBot'
import RequiredEmailConfirmation from '@modals/RequiredEmailConfirmation/RequiredEmailConfirmation'
import { dynamicImport } from '@/helpers/new/dynamicImport'
import { prepareGoogleTranslationForRoute } from '@common/Header/components/CustomGoogleTranslate/googleTranslateService'

const RouteTranslationBoundary = ({ children }) => {
  const location = useLocation()

  useLayoutEffect(() => {
    prepareGoogleTranslationForRoute()
  }, [location.pathname, location.search])

  return children
}

const TranslatedRoute = ({ render, ...routeProps }) => (
  <Route
    {...routeProps}
    render={(props) => (
      <RouteTranslationBoundary>{render(props)}</RouteTranslationBoundary>
    )}
  />
)

const Auth = dynamicImport(() => import('@pages/Auth'))
const University2035Callback = dynamicImport(() => import('@pages/Auth/University2035Callback'))
const SignUpVk = dynamicImport(() => import('@pages/Auth/SignUpVk'))
const RestorePassword = dynamicImport(() => import('@pages/Auth/RestorePassword'))
const AuthApp = dynamicImport(() => import('@pages/Auth/app'), 'AuthApp')
const Register = dynamicImport(() => import('@pages/Auth/Register'))
const WrapperNewsViewing = dynamicImport(() => import('@pages/NewsViewing'))
const Survey = dynamicImport(() => import('@pages/MySurveys/Components/FollowingLink'))
const PurchasingCourseOrClassUsingLink = dynamicImport(
  () => import('@pages/PurchasingCourseOrClassUsingLink'),
)
const CoursesCatalog = dynamicImport(() => import('@pages/CoursesCatalog'))
const CostCalculatorWidget = dynamicImport(
  () => import('@pages/CostCalculatorWidget/CostCalculatorWidget'),
)
const PaymentForm = dynamicImport(() => import('@pages/PaymentForm'))
const ProfileView = dynamicImport(() => import('@pages/ProfileView'))
const AuthorizationViaLink = dynamicImport(() =>
  import('@pages/AuthorizationViaLink'),
)
const WorkLink = dynamicImport(() =>
  import('@pages/HomeWork/WorkLink'),
)
const PaymentWithoutRegistration = dynamicImport(() =>
  import('../components/pages/CoursesCatalog/PaymentWithoutRegistration'),
)
const PublicPurposePayment = dynamicImport(() =>
  import('@pages/Payment/PublicPurposePayment'),
)
const PublicLibraryShare = dynamicImport(() =>
  import('@pages/Library/PublicLibraryShare'),
)

const Routing = () => {
  useFcmMessaging()
  const profile = useSelector(selectProfile)
  const isAuth = useSelector(selectIsAuth)
  const location = useLocation()
  const isMobile = useScreenWidth(992)
  const { isTechSupport } = useIsTechSupport()
  const [routesRemountKey, setRoutesRemountKey] = useState(0)
  const href = window.location.href
  const isPreviewNoAuth = Boolean(~href.indexOf('preview-no-auth'))
  const isPublicLibraryShare = location.pathname.startsWith('/library/shared/')
  const shouldShowRequiredModals = profile?.is_public_homework !== true
  const suspenseFallbackStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
  }

  const suspenseFallback = (
    <main className="page-content" style={suspenseFallbackStyle}>
      <Preloader fullScreen="center" />
    </main>
  )

  useEffect(() => {
    const handleAccessRestored = () => {
      setRoutesRemountKey((key) => key + 1)
    }

    window.addEventListener(
      REQUIRED_DOCUMENTS_ACCESS_RESTORED_EVENT,
      handleAccessRestored,
    )

    return () => {
      window.removeEventListener(
        REQUIRED_DOCUMENTS_ACCESS_RESTORED_EVENT,
        handleAccessRestored,
      )
    }
  }, [])

  return (
    <>
      <Technical />
      {!isPublicLibraryShare && <HelpDeskEddyWidget />}
      {isAuth && !isPreviewNoAuth && !isPublicLibraryShare && (
        <Layout>
          <Suspense key={routesRemountKey} fallback={suspenseFallback}>
            {routes.map((route, i) => {
              const franchiseManagerDenied =
                profile?.role === 'franchise-manager' &&
                ['/platform-settings', '/v2/platform-settings', '/license-and-payment'].includes(
                  route.path,
                )
              const helpdeskManagerAccess = route.path === '/helpdesk' && isTechSupport
              let access =
                !franchiseManagerDenied &&
                (
                  helpdeskManagerAccess ||
                  !(route.forRole && !route.forRole.includes(profile?.role)) ||
                  profile?.role === 'admin' ||
                  profile?.role === 'junior-admin' ||
                  profile?.role === 'franchise-manager'
                )
              const Component = access
                ? route.noMobile && isMobile
                  ? NoMobile
                  : route.components
                : NoAccess

              return (
                <TranslatedRoute
                  exact={route.exact}
                  path={route.path}
                  key={i}
                  render={() =>
                    route.path === '/home-work' &&
                    [
                      'admin',
                      'junior-admin',
                      'seminarian',
                      'senior-seminarian',
                    ].includes(profile?.role) ? (
                      <Redirect to="/dz-students" />
                    ) : (
                      <Component {...route.props} />
                    )
                  }
                />
              )
            })}
          </Suspense>
          {shouldShowRequiredModals && (
            <>
              <RequiredSurveyModal />
              <RequiredEmailConfirmation />
              <RequiredNumberConfirmationModal />
              <RequiredActivateTelegramBot />
              <RequiredActivateTelegramNotificationBot />
              <RequiredActivateVkNotificationCommunity />
              <RequiredActivateMaxNotificationBot />
              <RequiredActivateMaxBot />
              <RequiredLessonReportModal />
              <RequiredDocumentsComplianceModal />
            </>
          )}
        </Layout>
      )}
      {isPreviewNoAuth && (
        <NoAuthLayout>
          <Suspense fallback={suspenseFallback}>
            {previewRoutes.map((route, i) => {
              const franchiseManagerDenied =
                profile?.role === 'franchise-manager' &&
                ['/platform-settings', '/v2/platform-settings', '/license-and-payment'].includes(
                  route.path,
                )
              const helpdeskManagerAccess = route.path === '/helpdesk' && isTechSupport
              let access =
                !franchiseManagerDenied &&
                (
                  helpdeskManagerAccess ||
                  !(route.forRole && !route.forRole.includes(profile?.role)) ||
                  profile?.role === 'admin' ||
                  profile?.role === 'junior-admin' ||
                  profile?.role === 'franchise-manager'
                )
              const Component = access
                ? route.noMobile && isMobile
                  ? NoMobile
                  : route.components
                : NoAccess

              return (
                <TranslatedRoute
                  exact={route.exact}
                  path={route.path}
                  key={i}
                  render={() => <Component {...route.props} />}
                />
              )
            })}
          </Suspense>
          {shouldShowRequiredModals && (
            <>
              <RequiredSurveyModal />
              <RequiredNumberConfirmationModal />
            </>
          )}
        </NoAuthLayout>
      )}
      <Suspense fallback={suspenseFallback}>
        <TranslatedRoute exact={true} path="/auth" render={() => <Auth />} />
        <TranslatedRoute exact={true} path="/auth/university2035/callback" render={() => <University2035Callback />} />

        <TranslatedRoute exact={true} path="/register" render={() => <Register />} />
        <TranslatedRoute exact={true} path="/signup-vk" render={() => <SignUpVk />} />
        <TranslatedRoute
          exact={true}
          path="/restore-password"
          render={() => <RestorePassword />}
        />
        <TranslatedRoute exact={true} path="/auth/app" render={() => <AuthApp />} />
        <TranslatedRoute
          exact={true}
          path="/calculator"
          render={() => <CostCalculatorWidget />}
        />
        <TranslatedRoute
          path="/payment-without-registration"
          render={() => <PaymentWithoutRegistration />}
        />
        <TranslatedRoute
          exact={true}
          path="/purpose-payment/:paymentId"
          render={() => <PublicPurposePayment />}
        />
        <TranslatedRoute
          exact={true}
          path="/home-work/work-link"
          render={() => <WorkLink />}
        />
        <TranslatedRoute
          exact={true}
          path="/library/shared/:token"
          render={() => <PublicLibraryShare />}
        />
        {!isAuth && (
          <>
            <TranslatedRoute
              exact={true}
              path="/news/:id"
              label="Новость"
              render={() => <WrapperNewsViewing />}
            />
            <TranslatedRoute
              exact={true}
              path="/my-survey-link/:id"
              render={() => <Survey />}
            />
            <TranslatedRoute
              exact={true}
              path="/purchasing-course-or-class-using-link"
              render={() => <PurchasingCourseOrClassUsingLink />}
            />
            <TranslatedRoute path="/courses/all" render={() => <CoursesCatalog />} />
            <TranslatedRoute
              exact={true}
              path="/payment-form"
              render={() => <PaymentForm />}
            />
            <TranslatedRoute
              exact={true}
              path="/authorization-via-link-by-params"
              render={() => <AuthorizationViaLink />}
            />
            <TranslatedRoute
              exact={true}
              path="/profile-view"
              render={() => <ProfileView />}
            />
          </>
        )}
      </Suspense>
    </>
  )
}

export default Routing
